import { useState, useEffect, useCallback } from "react";
import { checkNewUser, addNewUser } from '../../utils/api.js';
import CustomSelect from '../shared/CustomSelect';
import AddNewSiteModal from '../../pages/AddNewSiteModal';

import HelpButton from '../shared/HelpButton';
import ContractViewer from '../ContractViewer';

// --- Constants ---
const EMPLOYMENT_TYPES = ["Employee", "Partnership", "Company", "Sole_Trader"];
const POSITIONS = ["Full Time", "Part Time","Casual"];
// Ordered Mon → Sun so the submitted string is always chronological
const WEEKDAYS = ["Mon", "Tues", "Wed", "Thurs", "Fri", "Sat", "Sun"];

const FULL_TIME_DEFAULTS = {
  weeklyHours: "38",
  superPayment: "472.85",
  grossSalary: "4681.12",
};

const PART_TIME_DEFAULTS = {
  weeklyHours: "19",
  superPayment: "236.43",
  grossSalary: "2340.56",
};

// Work Status is locked based on employment type
const WORK_STATUS_MAP = {
  Employee: "TFN",
  Partnership: "ABN",
  Company: "ABN",
  Sole_Trader: "ABN",
};

// --- Field config per employment type ---
const EXTRA_FIELDS_BY_TYPE = {
  Employee: [
    "siteWorking",
    "employee",
    "position",
    "weeklyHours",
    "superPayment",
    "grossSalary",
  ],
  Partnership: ["siteWorking", "partnership", "position"],
  Company: ["siteWorking", "company", "position"],
  Sole_Trader: ["siteWorking", "soleTrader", "declineTfn", "position"],
};

// --- Helpers ---
const formatMobile = (raw) => {
  const digits = raw.replace(/\D/g, "").slice(0, 10);
  if (digits.length === 0) return "";
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6)
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const isValidMobile = (val) => /^\(\d{3}\) \d{3}-\d{4}$/.test(val);
const isValidEmail = (val) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);

// ===== MAIN COMPONENT =====
const OnboardingForm = () => {
  const [formData, setFormData] = useState({
    typeOfEmployment: "Employee",
    snlType: "Newuser",
    workStatus: "TFN",
    email: "",
    mobile: "",
    firstName: "",
    siteWorking: "",
    formComplete: "New",
    employee: "New",
    partnership: "New",
    company: "New",
    soleTrader: "New",
    declineTfn: "New",
    position: "Full Time",
    weeklyHours: FULL_TIME_DEFAULTS.weeklyHours,
    superPayment: FULL_TIME_DEFAULTS.superPayment,
    grossSalary: FULL_TIME_DEFAULTS.grossSalary,
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState(new Set());
  const [checkStatus, setCheckStatus] = useState("idle");
  const [existingRecord, setExistingRecord] = useState(null);
  const [isChecked, setIsChecked] = useState(false);
  const [toast, setToast] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);

  // Working days (Employee only) — array of day keys from WEEKDAYS
  const [selectedDays, setSelectedDays] = useState([]);

  // --- Sites list (autocomplete-only via CustomSelect) ---
  const [sitesList, setSitesList] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(true);

  // --- Add New Site modal ---
  const [showAddSiteModal, setShowAddSiteModal] = useState(false);

 
  // Fetch sites list (endpoint returns sorted, de-duplicated array of strings)
  useEffect(() => {
    let cancelled = false;
    const fetchSites = async () => {
      try {
        setSitesLoading(true);
        const res = await fetch('/my/sites');
        const data = await res.json();
        if (cancelled) return;
        setSitesList(Array.isArray(data) ? data.filter(Boolean) : []);
      } catch (err) {
        console.error('Failed to load sites:', err);
        if (!cancelled) setSitesList([]);
      } finally {
        if (!cancelled) setSitesLoading(false);
      }
    };
    fetchSites();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  useEffect(() => {
    const ws = WORK_STATUS_MAP[formData.typeOfEmployment];
    setFormData((prev) => ({ ...prev, workStatus: ws }));
  }, [formData.typeOfEmployment]);

  // Update employee financials when position changes
  useEffect(() => {
    if (formData.typeOfEmployment === "Employee") {
      const defaults =
        formData.position === "Full Time"
          ? FULL_TIME_DEFAULTS
          : PART_TIME_DEFAULTS;
      setFormData((prev) => ({
        ...prev,
        weeklyHours: defaults.weeklyHours,
        superPayment: defaults.superPayment,
        grossSalary: defaults.grossSalary,
      }));
    }
  }, [formData.position, formData.typeOfEmployment]);

  // Reset check status when key fields change
  useEffect(() => {
    setCheckStatus("idle");
    setExistingRecord(null);
    setIsChecked(false);
  }, [formData.workStatus, formData.email, formData.mobile]);

  const setValue = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setTouched((prev) => new Set([...prev, field]));
  };

  // Toggle a working day on/off (Employee only)
  const toggleDay = (day) => {
    setSelectedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  // --- Check Status ---
  const handleCheckStatus = async () => {
    setCheckStatus("checking");
    try {
      const data = await checkNewUser({
        workStatus: formData.workStatus,
        email: formData.email,
        mobile: formData.mobile,
      });

      if (data && data.length > 0) {
        const record = {
          type: formData.workStatus,
          identifier: data[0][0],
          firstName: data[0][4],
          email: data[0][2],
          mobile: data[0][3],
          siteWorking: "",
          snlType: data[0][1],
          typeOfEmployment: data[0][5],
          workStatus: data[0][6],
        };
        setExistingRecord(record);
        setCheckStatus("found");
      } else {
        setCheckStatus("notFound");
      }
    } catch (err) {
      console.error("Check status failed:", err);
      setCheckStatus("notFound");
    }
    setIsChecked(true);
  };

  // --- Field handlers ---
  const handleMobileChange = (e) => {
    const formatted = formatMobile(e.target.value);
    setValue("mobile", formatted);
    if (formatted && !isValidMobile(formatted)) {
      setErrors((prev) => ({ ...prev, mobile: "Format must be (0XX) XXX-XXXX" }));
    } else if (!formatted) {
      setErrors((prev) => ({ ...prev, mobile: "Mobile is required" }));
    } else {
      setErrors((prev) => { const n = { ...prev }; delete n.mobile; return n; });
    }
  };

  const handleEmailChange = (e) => {
    const val = e.target.value;
    setValue("email", val);
    if (!val.trim()) {
      setErrors((prev) => ({ ...prev, email: "Email is required" }));
    } else if (!isValidEmail(val)) {
      setErrors((prev) => ({ ...prev, email: "Enter a valid email address" }));
    } else {
      setErrors((prev) => { const n = { ...prev }; delete n.email; return n; });
    }
  };

  const handleFirstNameChange = (e) => {
    const val = e.target.value;
    setValue("firstName", val);
    if (!val.trim()) {
      setErrors((prev) => ({ ...prev, firstName: "First name is required" }));
    } else if (/\d/.test(val)) {
      setErrors((prev) => ({ ...prev, firstName: "First name should not contain numbers" }));
    } else {
      setErrors((prev) => { const n = { ...prev }; delete n.firstName; return n; });
    }
  };

  // --- Site Working — autocomplete-only via CustomSelect ---
  const handleSiteWorkingChange = (val) => {
    setValue("siteWorking", val);
    setErrors((prev) => { const n = { ...prev }; delete n.siteWorking; return n; });
  };

  // --- Add New Site: called by modal on successful POST ---
  // Appends to sitesList (sorted, deduped) and auto-selects the new site so
  // the admin can continue filling the rest of the form.
  const handleSiteCreated = (newSiteName) => {
    setSitesList((prev) => {
      // dedupe (case-insensitive) and sort to match endpoint behavior
      const seen = new Set(prev.map((s) => s.toLowerCase()));
      if (seen.has(newSiteName.toLowerCase())) return prev;
      return [...prev, newSiteName].sort((a, b) => a.localeCompare(b));
    });
    setValue("siteWorking", newSiteName);
    setErrors((prev) => { const n = { ...prev }; delete n.siteWorking; return n; });
    setToast({ type: 'success', message: `"${newSiteName}" added and selected.` });
  };

  const handleMoneyChange = (field, e, maxLength) => {
    const val = e.target.value.replace(/[^0-9.]/g, "");
    const trimmed = maxLength ? val.slice(0, maxLength) : val;
    setValue(field, trimmed);
    if (!trimmed) {
      setErrors((prev) => ({ ...prev, [field]: "This field is required" }));
    } else if (isNaN(Number(trimmed))) {
      setErrors((prev) => ({ ...prev, [field]: "Must be a valid number" }));
    } else {
      setErrors((prev) => { const n = { ...prev }; delete n[field]; return n; });
    }
  };

  // --- Can check ---
  const canCheck =
    formData.workStatus &&
    formData.email &&
    isValidEmail(formData.email) &&
    formData.mobile &&
    isValidMobile(formData.mobile) &&
    formData.firstName.trim() &&
    !errors.firstName;

  // --- Extra fields ---
  const extraFields = EXTRA_FIELDS_BY_TYPE[formData.typeOfEmployment] || [];

  // --- Submit validation ---
  const getRequiredFields = useCallback(() => {
    const base = ["workStatus", "email", "mobile", "firstName"];
    if (!isChecked || checkStatus === "found") return base;
    const extra = extraFields.filter(
      (f) => !["employee", "partnership", "company", "soleTrader", "declineTfn", "formComplete"].includes(f)
    );
    return [...base, ...extra];
  }, [isChecked, checkStatus, extraFields]);

  const isFormValid = useCallback(() => {
    if (!isChecked) return false;
    if (checkStatus === "found") return false;
    const required = getRequiredFields();
    for (const field of required) {
      if (!formData[field] || !formData[field].toString().trim()) return false;
    }
    if (!isValidEmail(formData.email)) return false;
    if (!isValidMobile(formData.mobile)) return false;
    // Employees must have at least one working day selected
    if (formData.typeOfEmployment === "Employee" && selectedDays.length === 0) return false;
    if (Object.keys(errors).length > 0) return false;
    return true;
  }, [formData, errors, isChecked, checkStatus, getRequiredFields, selectedDays]);

  const handleSubmit = async () => {
  if (!isFormValid() || isSubmitting) return;
  setIsSubmitting(true);
  try {
    // Build WorkingDays as a Mon→Sun ordered, comma-separated string (Employee only)
    const payload = {
      ...formData,
      workingDays:
        formData.typeOfEmployment === "Employee"
          ? WEEKDAYS.filter((d) => selectedDays.includes(d)).join(",")
          : "",
    };
    const res = await addNewUser(payload);
      if (res?.emailSent) {
        setToast({ type: 'success', message: `Record created — invite emailed to ${formData.firstName} (CC Sofia).` });
      } else {
        setToast({ type: 'success', message: 'Record created, but the invite email failed — send it manually.' });
      }
      setTimeout(() => {
        window.location.href = '/adminfiltersd2';
      }, 2000);
  } catch (err) {
    console.error("Submit failed:", err);
    setToast({ type: 'error', message: 'Failed to create record. Please try again.' });
    setIsSubmitting(false);
  }
};

  // --- Entity label ---
  const entityFieldKey = {
    Employee: "employee",
    Partnership: "partnership",
    Company: "company",
    Sole_Trader: "soleTrader",
  }[formData.typeOfEmployment];

  const entityLabel = {
    Employee: "Employee",
    Partnership: "Partnership",
    Company: "Company",
    Sole_Trader: "Sole Trader",
  }[formData.typeOfEmployment];

  // --- Input class helper ---
  const inputClass = (field) =>
    `ob-input ${touched.has(field) && errors[field] ? "ob-input-error" : ""}`;

  const isFound = checkStatus === "found";

  return (
    <div className="ob-page">
      {toast && (
      <div className={`ob-toast ${toast.type === 'success' ? 'ob-toast-success' : 'ob-toast-error'}`}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          {toast.type === 'success' 
            ? <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            : <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          }
        </svg>
        {toast.message}
      </div>
    )}

    {/* Add New Site modal — completely isolated state, parent unaffected on cancel */}
    <AddNewSiteModal
      isOpen={showAddSiteModal}
      onClose={() => setShowAddSiteModal(false)}
      onCreated={handleSiteCreated}
    />

      <div className="ob-container">
        {/* Header */}
        <div className="ob-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px'}}>
          <h1 className="ob-title">On-boarding Form</h1>
          <HelpButton helpData={[
            { chip: 'onboardingform', urlFilter: '/static/pdffilters/OnboardingForm.pdf' }
          ]} />
          </div>
          <p className="ob-subtitle">Fill in all fields</p>
          {pdfUrl && (
          <ContractViewer pdfUrl={pdfUrl} onClose={() => setPdfUrl(null)} />
        )}
        </div>

        <div className="ob-form-card">
          <div className="ob-form-fields">

            {/* Type of Employment */}
            <div className="ob-field">
            <CustomSelect
              label="Type_Of_Employment"
              required
              value={formData.typeOfEmployment}
              options={EMPLOYMENT_TYPES}
              onChange={(val) => {
                setValue("typeOfEmployment", val);
                setCheckStatus("idle");
                setExistingRecord(null);
                setIsChecked(false);
                setSelectedDays([]);
              }}
            />
          </div>

            {/* SNL_Type - read only */}
            <div className="ob-field">
              <label className="ob-label">
                SNL_Type <span className="ob-label-required">*</span>
              </label>
              <div className="ob-badge-field">
                <span className="ob-badge ob-badge-green">Newuser</span>
              </div>
            </div>

            {/* Work Status - read only, auto-set */}
            <div className="ob-field">
              <label className="ob-label">
                Work Status <span className="ob-label-required">*</span>
              </label>
              <input
                type="text"
                value={formData.workStatus}
                readOnly
                className="ob-input ob-input-readonly"
              />
            </div>

            {/* Mobile */}
            <div className="ob-field">
              <label className="ob-label">
                Mobile <span className="ob-label-required">*</span>
              </label>
              <p className="ob-helper">
                Replace the # with the numbers preserving this format (040) 204-1011 thanks
              </p>
              <input
                type="text"
                inputMode="numeric"
                maxLength={14}
                value={formData.mobile}
                onChange={handleMobileChange}
                placeholder="(###) ###-####"
                disabled={isFound}
                className={isFound ? "ob-input ob-input-readonly" : inputClass("mobile")}
              />
              {touched.has("mobile") && errors.mobile && (
                <div className="ob-error">
                  <svg fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  {errors.mobile}
                </div>
              )}
            </div>

            {/* Email */}
            <div className="ob-field">
              <label className="ob-label">
                Email <span className="ob-label-required">*</span>
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={handleEmailChange}
                placeholder="youremail@yahoo.com.au"
                disabled={isFound}
                className={isFound ? "ob-input ob-input-readonly" : inputClass("email")}
              />
              {touched.has("email") && errors.email && (
                <div className="ob-error">
                  <svg fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  {errors.email}
                </div>
              )}
            </div>

            {/* First Name */}
            <div className="ob-field">
              <label className="ob-label">
                First_Name <span className="ob-label-required">*</span>
              </label>
              <input
                type="text"
                value={formData.firstName}
                onChange={handleFirstNameChange}
                placeholder="No Data"
                disabled={isFound}
                className={isFound ? "ob-input ob-input-readonly" : inputClass("firstName")}
              />
              {touched.has("firstName") && errors.firstName && (
                <div className="ob-error">
                  <svg fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  {errors.firstName}
                </div>
              )}
            </div>

            {/* Check Status Button */}
            {!isChecked && (
              <button
                onClick={handleCheckStatus}
                disabled={!canCheck || checkStatus === "checking"}
                className="ob-btn ob-btn-check"
              >
                {checkStatus === "checking" ? (
                  <span className="ob-spinner">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Checking…
                  </span>
                ) : (
                  "Check Status"
                )}
              </button>
            )}

            {/* Existing record card */}
            {isFound && existingRecord && (
              <div className="ob-record-card">
                <div className="ob-record-header">
                  <div className="ob-record-icon">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="ob-record-title">
                    Existing {existingRecord.type} Record Found
                  </h3>
                </div>
                <div className="ob-record-grid">
                  <div className="ob-record-item">
                    <div className="ob-record-item-label">{existingRecord.type}</div>
                    <div className="ob-record-item-value">{existingRecord.identifier}</div>
                  </div>
                  <div className="ob-record-item">
                    <div className="ob-record-item-label">Name</div>
                    <div className="ob-record-item-value">{existingRecord.firstName}</div>
                  </div>
                  <div className="ob-record-item">
                    <div className="ob-record-item-label">Email</div>
                    <div className="ob-record-item-value">{existingRecord.email}</div>
                  </div>
                  <div className="ob-record-item">
                    <div className="ob-record-item-label">Mobile</div>
                    <div className="ob-record-item-value">{existingRecord.mobile}</div>
                  </div>
                </div>
              </div>
            )}

            {/* === PHASE 2: Extra fields === */}
            {isChecked && checkStatus === "notFound" && (
              <>
                <div className="ob-divider" />
                <div className="ob-info-banner ob-info-banner-warning">
                  No existing {formData.workStatus} record found. Please complete the remaining fields.
                </div>

                {/* Site Working — autocomplete-only via CustomSelect */}
                {extraFields.includes("siteWorking") && (
                  <div className="ob-field">
                    <CustomSelect
                      label="Site Working"
                      required
                      value={formData.siteWorking}
                      options={sitesList}
                      onChange={handleSiteWorkingChange}
                    />
                    {sitesLoading && (
                      <div className="ob-helper">Loading sites…</div>
                    )}
                    {!sitesLoading && sitesList.length === 0 && (
                      <div className="ob-helper" style={{ color: '#f87171' }}>
                        No sites available. Use "Add New Site" below to create one.
                      </div>
                    )}

                    {/* Add New Site button — shown while no site is selected */}
                    {!formData.siteWorking && !sitesLoading && (
                      <button
                        type="button"
                        onClick={() => setShowAddSiteModal(true)}
                        className="ob-btn"
                        style={{
                          marginTop: '8px',
                          background: 'transparent',
                          border: '1px dashed rgba(34,211,238,0.4)',
                          color: '#22d3ee',
                          padding: '8px 14px',
                          fontSize: '0.9rem',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                        }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <line x1="12" y1="5" x2="12" y2="19" />
                          <line x1="5" y1="12" x2="19" y2="12" />
                        </svg>
                        Add New Site
                      </button>
                    )}

                    {touched.has("siteWorking") && errors.siteWorking && (
                      <div className="ob-error">
                        <svg fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        {errors.siteWorking}
                      </div>
                    )}
                  </div>
                )}

                {/* FormComplete - read only */}
                <div className="ob-field">
                  <label className="ob-label">
                    FormComplete <span className="ob-label-required">*</span>
                  </label>
                  <div className="ob-badge-field">
                    <span className="ob-badge ob-badge-blue">New</span>
                  </div>
                </div>

                {/* Entity field - read only */}
                {entityFieldKey &&
                  extraFields.includes(entityFieldKey) &&
                  !(formData.typeOfEmployment === "Employee" && formData.position === "Casual") && (
                  <div className="ob-field">
                    <label className="ob-label">
                      {entityLabel} <span className="ob-label-required">*</span>
                    </label>
                    <div className="ob-badge-field">
                      <span className="ob-badge ob-badge-blue">New</span>
                    </div>
                  </div>
                )}
                {/* Decline_TFN - Sole Trader only */}
                {extraFields.includes("declineTfn") && (
                  <div className="ob-field">
                    <label className="ob-label">
                      Decline_TFN <span className="ob-label-required">*</span>
                    </label>
                    <div className="ob-badge-field">
                      <span className="ob-badge ob-badge-blue">New</span>
                    </div>
                  </div>
                )}

                {/* Position */}
                {extraFields.includes("position") && (
                  <div className="ob-field">
                    <CustomSelect
                      label="Position"
                      required
                      value={formData.position}
                      options={POSITIONS}
                      onChange={(val) => setValue("position", val)}
                    />
                  </div>
                )}

                {/* Working Days — Employee only. Highlighted days are submitted
                    as a Mon→Sun ordered, comma-separated WorkingDays string. */}
                {formData.typeOfEmployment === "Employee" && (
                  <div className="ob-field">
                    <label className="ob-label">
                      Working Days <span className="ob-label-required">*</span>
                    </label>
                    <p className="ob-helper">Tap each day the employee works.</p>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginTop: "6px" }}>
                      {WEEKDAYS.map((day) => {
                        const active = selectedDays.includes(day);
                        return (
                          <button
                            key={day}
                            type="button"
                            onClick={() => toggleDay(day)}
                            aria-pressed={active}
                            style={{
                              padding: "8px 14px",
                              borderRadius: "8px",
                              fontSize: "0.9rem",
                              fontWeight: active ? 600 : 500,
                              cursor: "pointer",
                              border: active
                                ? "1px solid #22d3ee"
                                : "1px solid rgba(148,163,184,0.4)",
                              background: active ? "#22d3ee" : "transparent",
                              color: active ? "#0f172a" : "#cbd5e1",
                              transition: "all 0.15s ease",
                            }}
                          >
                            {day}
                          </button>
                        );
                      })}
                    </div>
                    {selectedDays.length === 0 && (
                      <div className="ob-helper" style={{ color: "#f87171", marginTop: "6px" }}>
                        Select all working days.
                      </div>
                    )}
                  </div>
                )}

                {/* Employee financial fields */}
                {formData.typeOfEmployment === "Employee" && (
                  <>
                    <div className="ob-field">
                      <label className="ob-label">Weekly Hours</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={formData.weeklyHours}
                        onChange={(e) => handleMoneyChange("weeklyHours", e, 5)}
                        className={inputClass("weeklyHours")}
                      />
                      {touched.has("weeklyHours") && errors.weeklyHours && (
                        <div className="ob-error">{errors.weeklyHours}</div>
                      )}
                    </div>

                    <div className="ob-field">
                      <label className="ob-label">Super Payment</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={formData.superPayment}
                        onChange={(e) => handleMoneyChange("superPayment", e, 10)}
                        placeholder="0.00"
                        className={inputClass("superPayment")}
                      />
                      {touched.has("superPayment") && errors.superPayment && (
                        <div className="ob-error">{errors.superPayment}</div>
                      )}
                    </div>

                    <div className="ob-field">
                      <label className="ob-label">Gross Salary</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={formData.grossSalary}
                        onChange={(e) => handleMoneyChange("grossSalary", e, 10)}
                        placeholder="0.00"
                        className={inputClass("grossSalary")}
                      />
                      {touched.has("grossSalary") && errors.grossSalary && (
                        <div className="ob-error">{errors.grossSalary}</div>
                      )}
                    </div>
                  </>
                )}

                {/* Submit */}
                <button
                  onClick={handleSubmit}
                  disabled={!isFormValid() || isSubmitting}
                  className="ob-btn ob-btn-submit"
                >
                  {isSubmitting ? (
                    <span className="ob-spinner">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Submitting…
                    </span>
                  ) : (
                    "Submit"
                  )}
                    </button>
              </>
            )}
          </div>
        </div>

        <p className="ob-footer">On-boarding Form</p>
      </div>
    </div>
  );
};
export default OnboardingForm;